import { redirect } from 'next/navigation';
import { ApiErrorResponse } from '@type/api/apiUtilityFn';

const normalizeApiErrorMessage = (message: string | undefined, response: Response) => {
  const trimmedMessage = message?.trim().replace(/\s+/g, ' ');

  if (trimmedMessage) {
    return trimmedMessage.slice(0, 300);
  }

  return response.statusText || 'Unexpected API error';
};

export const parseApiErrorResponse = async (response: Response): Promise<ApiErrorResponse> => {
  const responseClone = response.clone();
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    try {
      return await response.json() as ApiErrorResponse;
    } catch {
      // Fall back to text parsing when the backend returns invalid JSON.
    }
  }

  const rawBody = await responseClone.text().catch(() => '');

  return {
    errors: [
      {
        code: response.status,
        message: normalizeApiErrorMessage(rawBody, response),
      },
    ],
  };
};

/**
 * @description apiFetch function to handle special error codes from the API.
 * - `response`: *Response* - The response object from the API.
 */
export const specialErrorCodesApiFetch = async (
  response: Response,
) => {
  // Parse the error response
  if (!response.ok) {
    const error = await parseApiErrorResponse(response);
    const firstError = error?.errors?.[0] ? error.errors[0] : undefined;
    const firstCode = firstError?.code;
    let redirectUrl: string | undefined;

    // Handle special error codes
    switch (firstCode) {
      case 862:
        // Handle 862 error code - Account is blocked for accumulated debts
        redirectUrl = '/account-blocked';
        break;
      default:
        // Not a special error code
        break;
    }

    // Redirect if a URL is defined
    if (redirectUrl) redirect(redirectUrl);
  }
};
